import { useRef, useState, type ReactNode } from 'react'

import { DashCard } from './DashboardCards'
import { hexA } from '../../../lib/lens'

/* Plot geometry — the SVG stretches to the card (preserveAspectRatio="none"), so all text/dots are
   HTML overlays positioned by percentage (SVG text would distort with the stretch). */
const W = 320
const H = 96
const P = 8

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const BUCKET_CAPTION: Record<string, string> = { day: 'Daily', week: 'Weekly', month: 'Monthly' }

/** "2026-05-18" → "May 18" (or "May" for month buckets). Hand-parsed to avoid locale/TZ drift. */
function fmtBucket(iso: string, bucketSize: string): string {
  const [, m, d] = iso.split('-')
  const mon = MONTHS_SHORT[Number(m) - 1] ?? ''
  return bucketSize === 'month' ? mon : `${mon} ${Number(d)}`
}

/** Up to ~6 evenly-spaced tick labels, always including the last bucket. */
function tickLabels(buckets: string[], bucketSize: string): string[] {
  if (buckets.length === 0) {
    return []
  }
  const max = 6
  const step = buckets.length <= max ? 1 : Math.ceil(buckets.length / max)
  const picked = buckets.filter((_, i) => i % step === 0)
  const last = buckets[buckets.length - 1]!
  if (picked[picked.length - 1] !== last) {
    picked.push(last)
  }
  return picked.map((b) => fmtBucket(b, bucketSize))
}

interface HoverPoint {
  idx: number
  v: number
  leftPct: number
  topPct: number
}

/** Resolve the hovered index into a renderable point (plot-percentage coords), or null. */
function hoverPoint(
  values: (number | null)[],
  hoverIdx: number | null,
  x: (i: number) => number,
  y: (v: number) => number,
): HoverPoint | null {
  if (hoverIdx === null) {
    return null
  }
  const v = values[hoverIdx]
  if (v === null || v === undefined) {
    return null
  }
  return { idx: hoverIdx, v, leftPct: (x(hoverIdx) / W) * 100, topPct: (y(v) / H) * 100 }
}

/** Nearest index with a real value (search outward from `idx`); null when the series is all-null. */
function nearestReal(values: (number | null)[], idx: number): number | null {
  const real = (i: number): boolean => i >= 0 && i < values.length && values[i] !== null
  for (let d = 0; d < values.length; d++) {
    if (real(idx - d)) {
      return idx - d
    }
    if (real(idx + d)) {
      return idx + d
    }
  }
  return null
}

interface DashTrendCardProps {
  title: string
  values: (number | null)[]
  buckets: string[]
  bucketSize: string
  accent: string
  /** '%' for coverage, '' for counts — appended to every rendered value. */
  unit: '%' | ''
  /** Period movement badge (a `DeltaBadge`); omitted when the scope can't support one. */
  delta?: ReactNode
}

/**
 * The Dashboard's readable trend card: headline (latest value + period movement), an area line you
 * can HOVER to read any bucket's exact value (crosshair + tooltip + dot), min/max scale labels, and
 * date ticks. Replaces the bare sparkline — a chart you can pull numbers from, not just a shape.
 */
export function DashTrendCard({ title, values, buckets, bucketSize, accent, unit, delta }: DashTrendCardProps) {
  const plotRef = useRef<HTMLDivElement>(null)
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)

  const nums = values.filter((v): v is number => v !== null)
  const empty = buckets.length === 0 || nums.length === 0
  const caption = BUCKET_CAPTION[bucketSize] ?? 'Trend'

  if (empty) {
    return (
      <DashCard title={title} caption={caption}>
        <div className="py-8 text-center text-[11.5px] text-gis-soft">No trend data for this period.</div>
      </DashCard>
    )
  }

  const min = Math.min(...nums)
  const max = Math.max(...nums)
  const span = Math.max(1, values.length - 1)
  const x = (i: number): number => P + (i * (W - 2 * P)) / span
  const y = (v: number): number => H - P - ((v - min) / (max - min || 1)) * (H - 2 * P)
  const pts = values
    .map((v, i) => (v === null ? null : `${x(i).toFixed(1)},${y(v).toFixed(1)}`))
    .filter((p): p is string => p !== null)
    .join(' ')
  const fmtVal = (v: number): string => `${Math.round(v * 10) / 10}${unit}`
  const lastIdx = values.length - 1 - [...values].reverse().findIndex((v) => v !== null)
  const latest = values[lastIdx]!

  const onMove = (clientX: number): void => {
    const rect = plotRef.current?.getBoundingClientRect()
    if (!rect || rect.width === 0) {
      return
    }
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    setHoverIdx(nearestReal(values, Math.round(frac * span)))
  }

  const hover = hoverPoint(values, hoverIdx, x, y)

  return (
    <DashCard title={title} caption={caption}>
      {/* Headline: the latest bucket's value + the period movement — the number the chart ends on. */}
      <div className="mb-1 flex items-baseline gap-2">
        <b className="text-[20px] leading-none font-bold tracking-[-.01em] tabular-nums text-gis-ink">
          {fmtVal(latest)}
        </b>
        {delta}
        <span className="ml-auto text-[10px] text-gis-muted">as of {fmtBucket(buckets[lastIdx]!, bucketSize)}</span>
      </div>

      <div
        ref={plotRef}
        className="relative"
        onPointerMove={(e) => onMove(e.clientX)}
        onPointerLeave={() => setHoverIdx(null)}
      >
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block h-24 w-full">
          {/* recessive scale gridlines (min / mid / max) */}
          {[y(max), y((min + max) / 2), y(min)].map((gy) => (
            <line key={gy} x1={P} x2={W - P} y1={gy} y2={gy} stroke="var(--gis-line)" strokeDasharray="3 4" />
          ))}
          <polygon points={`${P},${H - P} ${pts} ${W - P},${H - P}`} fill={hexA(accent, 0.12)} />
          <polyline points={pts} fill="none" stroke={accent} strokeWidth="2" />
          {hover && (
            <line x1={x(hover.idx)} x2={x(hover.idx)} y1={P - 2} y2={H - P} stroke={hexA(accent, 0.45)} strokeWidth="1" />
          )}
        </svg>

        {/* HTML overlays — scale labels, the hover dot and the value tooltip (never distorted). */}
        <span className="pointer-events-none absolute top-0 left-1 text-[9px] font-medium tabular-nums text-gis-muted">
          {fmtVal(max)}
        </span>
        <span className="pointer-events-none absolute bottom-0 left-1 text-[9px] font-medium tabular-nums text-gis-muted">
          {fmtVal(min)}
        </span>
        {hover && (
          <>
            <span
              className="pointer-events-none absolute h-[9px] w-[9px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-gis-card"
              style={{ left: `${hover.leftPct}%`, top: `${hover.topPct}%`, background: accent }}
            />
            <span
              className="pointer-events-none absolute -top-1 -translate-x-1/2 rounded-[7px] bg-gis-navy px-[7px] py-[2px] text-[10px] font-bold whitespace-nowrap text-white tabular-nums"
              style={{ left: `${Math.min(82, Math.max(18, hover.leftPct))}%` }}
            >
              {fmtBucket(buckets[hover.idx]!, bucketSize)} · {fmtVal(hover.v)}
            </span>
          </>
        )}
      </div>

      <div className="flex justify-between px-1 pt-1 text-[9px] text-gis-muted">
        {tickLabels(buckets, bucketSize).map((t, i) => (
          <span key={`${t}-${i}`}>{t}</span>
        ))}
      </div>
    </DashCard>
  )
}
